import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { BayclubsAlertService } from '@bayclubs/alert';

import { camps } from '../../../models/models.generated';
import { ReportsApiService } from '../../../services/api/reports-api.service';
import { ReportsEventService, ReportType } from '../reports-event.service';
import { EnrollmentReportFilter } from '../../../models/filters/reports-filters.model';
import { FiltersHelperService } from '../../../services/filters-helper.service';

@Component({
    selector: 'app-enrollment-report',
    templateUrl: './enrollment-report.component.html',
    styleUrls: ['./enrollment-report.component.scss'],
    standalone: false
})
export class EnrollmentReportComponent implements OnInit, OnDestroy {
    public searchForm: UntypedFormGroup;

    public items: camps.EnrollmentReportInfoItem[] = [];
    // Rendering tens of thousands of rows freezes the page; the full list is in the generated file
    public visibleItems: camps.EnrollmentReportInfoItem[] = [];
    private readonly pageSize = 500;
    public isBusy: boolean = false;
    public useV2: boolean = false;

    private onSearchSubscription;

    public filters: EnrollmentReportFilter;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private reportsApi: ReportsApiService,
        private alert: BayclubsAlertService,
        private reportsEvent: ReportsEventService,
        private filtersHelperService: FiltersHelperService
    ) {
        this.filters = this.getFilter();
    }

    ngOnInit() {
        this.initForm(this.filters);
    }

    ngOnDestroy() {
        if (this.onSearchSubscription) {
            this.onSearchSubscription.unsubscribe();
            this.onSearchSubscription = null;
        }
    }

    private initForm(filter: EnrollmentReportFilter): void {
        this.searchForm = this.formBuilder.group({
            dateFrom: [filter.dateFrom, []],
            dateTo: [filter.dateTo, []],
            sessionName: [filter.sessionName, []],
            clubIds: [filter.clubIds, []],
            categories: [filter.categories, []],
            sessionDateFrom: [filter.sessionDateFrom, []],
            sessionDateTo: [filter.sessionDateTo, []],
            campTypes: [filter.campTypes, []],
            timeFrom: [filter.dateFrom ? filter.timeFrom : 0, []],
            timeTo: [filter.dateTo ? filter.timeTo : 0, []]
        });

        this.onSearchSubscription = this.reportsEvent.search.subscribe((useV2: boolean) => {
            this.onSearch(useV2);
        });
    }

    public onSearch(useV2: boolean = false) {
        const request = this.buildRequest();

        this.isBusy = true;
        this.useV2 = useV2;

        this.reportsApi.searchForEnrollmentReport(request, useV2).subscribe(
            x => {
                this.items = x.value.items;
                this.visibleItems = (this.items ?? []).slice(0, this.pageSize);

                const filter: EnrollmentReportFilter = <EnrollmentReportFilter>{
                    dateFrom: this.searchForm.controls['dateFrom'].value,
                    dateTo: this.searchForm.controls['dateTo'].value,
                    sessionName: this.searchForm.controls['sessionName'].value,
                    clubIds: this.searchForm.controls['clubIds'].value,
                    categories: this.searchForm.controls['categories'].value,
                    campTypes: this.searchForm.controls['campTypes'].value,
                    sessionDateFrom: this.searchForm.controls['sessionDateFrom'].value,
                    sessionDateTo: this.searchForm.controls['sessionDateTo'].value,
                    reportType: ReportType.Enrollment,
                    timeFrom: this.searchForm.controls['timeFrom'].value,
                    timeTo: this.searchForm.controls['timeTo'].value,
                };

                this.filtersHelperService.mergeQueryParam(filter, 'EnrollmentReport', 'enrollmentFilter');
            },
            error => {
                this.alert.showError(error);
            },
            () => {
                this.isBusy = false;
            }
        );
    }

    public showMore() {
        this.visibleItems = this.items.slice(0, this.visibleItems.length + this.pageSize);
    }

    public get noItems() {
        return this.items == null || this.items.length === 0;
    }

    public getReport() {
        const request = this.buildRequest();
        this.reportsApi.getEnrollmentReport(request, this.useV2);
    }

    private buildRequest(): camps.EnrollmentReportRequest {
        let request = <camps.EnrollmentReportRequest>{
            dateFrom: this.searchForm.controls['dateFrom'].value,
            dateTo: this.searchForm.controls['dateTo'].value,
            categories: this.searchForm.controls['categories'].value,
            clubs: this.searchForm.controls['clubIds'].value,
            sessionName: this.searchForm.controls['sessionName'].value,
            sessionDateFrom: this.searchForm.controls['sessionDateFrom'].value,
            sessionDateTo: this.searchForm.controls['sessionDateTo'].value,
            campTypes: this.searchForm.controls['campTypes'].value
        };

        request.timeFrom = request.dateFrom ? this.searchForm.controls['timeFrom'].value : null;
        request.timeTo = request.dateTo ? this.searchForm.controls['timeTo'].value : null;

        return request;
    }

    private getFilter() {
        const defaultFilter: EnrollmentReportFilter = {
            dateFrom: null,
            dateTo: null,
            sessionName: null,
            clubIds: [],
            categories: [],
            campTypes: [],
            sessionDateFrom: null,
            sessionDateTo: null,
            reportType: ReportType.Capacity,
            timeFrom: 0,
            timeTo: 0
        };

        const restoredFilter = this.filtersHelperService.restoreFilter<EnrollmentReportFilter>(
            'EnrollmentReport',
            'enrollmentFilter'
        );

        return restoredFilter ? restoredFilter : defaultFilter;
    }
}
